/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useGridApiRef } from '@mui/x-data-grid-pro';
import { Grid, Typography, IconButton, gridClasses, Tooltip } from '@mui/material';
import ModalDialog from '../ui/ModalDialog/index';
import { CancelPresentationRounded, DeleteRounded, PersonAddAlt, SaveRounded, ScatterPlot } from '@mui/icons-material';
import PropTypes from 'prop-types';
import styles from './style';
import Datagrid from '../ui/Datagrid';
import { renderCell } from '../ui/Datagrid/table_config';
import { addNewGeometryRow, createGeometry, deleteGeometryRow, importWellsGeometryCSVFIle, listWellsGeometry, updateGeometry } from '../../store/actions/wellsGeometry';
import columns from './table_config';
import SelectEditInputTextField from '../ui/SelectEditInputTextField';
import SelectEditValueTypes from '../ui/SelectEditValueTypes';
import { enqueueSnackbar } from '../../store/actions/snackbar';
import { APP_ACTION_TYPES, HYDRAULICS_ADMIN_ROLES, PERMISSION_DIALOG_SOURCES, TOOLTIPS, WELLS_GEOMETRY_TYPE } from '../../utils/constants';
import DeleteConfirmationModal from '../deleteConfirmationModal';
import useUnsavedChanges from '../../hooks/useUnsavedChanges';
import useUnitConversion from '../../hooks/useUnitConversion';
import { UNIT_SYSTEM } from '../../shared/config/hydraulics_constants';
import WellsObservationPointsModal from '../ui/HydraulicsModalDialog/WellsObservationPointsModal';
import ConfirmationModal from '../confirmationModal';
import PermissionsModal from '../permissions';
import { checkPermissions } from '../../utils/checkPermissions';
import CreatedByInfoDialog from '../ui/CreatedByInfoDialog';
import { REDUX_ACTIONS } from '../../shared/config/constants';
const GeometryModal = ({
  onCloseClick,
  geometryData,
  isOpen
}) => {
  const dispatch = useDispatch();
  const classes = styles();
  const geometry = useSelector((state) => state.wellsGeometry);
  const apiRef = useGridApiRef();
  const [properties, setProperties] = useState([]);
  const [invalidRows, setInvalidRows] = useState({});
  const [deleteAction, setDeleteAction] = useState(false);
  const [deleteRow, setDeleteRow] = useState();
  const { unit, convertUnitSystem } = useUnitConversion();
  const {
    unSavedExists,
    handleClose,
    UnsavedChangesModal
  } = useUnsavedChanges(properties, onCloseClick);
  const [wellsObservationPointsModal, setWellsObservationPointsModal] = useState(false);
  const holeDepth = properties?.length > 0 ? Math.max(...properties.map(({ shoe_md }) => shoe_md)) : '';
  const [saveOpenHoleID, setSaveOpenHoleID] = useState({
    open: false,
    data: {}
  });
  const disableAction = geometry?.loading || properties.some(item => item?.isDirty === true || item?.isNewRow);
  const user = useSelector((state) => state.currentUser);
  const [permissions, setPermissions] = useState({
    open: false,
    data: {}
  });
  const isHydraulicsAdmin = HYDRAULICS_ADMIN_ROLES.includes(user?.type);

  useEffect(() => {
    if (!geometry.loading) {
      dispatch(listWellsGeometry(geometryData?.row?._id, onCloseClick));
    }
    return () => {
      dispatch({
        type: REDUX_ACTIONS.WELLS_GEOMETRY_RESET
      });
    };
  }, []);

  useEffect(() => {
    if (geometry?.data) {
      setProperties(geometry?.data);
    }
  }, [geometry?.data]);

  useEffect(() => {
    if (properties[0]?.type === '') {
      const id = properties[0]._id;
      setTimeout(() => {
        apiRef.current.scrollToIndexes({
          rowIndex: 0
        });
        apiRef.current.setCellMode(id, 'type', 'edit');
        setTimeout(() => {
          apiRef.current.setCellFocus(id, 'type');
        }, 50);
      }, 50);
      apiRef.current.setPage(0);
    }
  }, [properties?.length]);

  const handleAddSurvey = () => {
    if (properties[0]?.isNewRow) return;
    dispatch(addNewGeometryRow());
  };

  const removeSurvey = (row) => {
    if (row?.isNewRow) {
      const updatedGeometry = properties.filter(item => item._id !== row?._id);
      setProperties(updatedGeometry);
      dispatch(listWellsGeometry(geometryData?.row?._id));
    } else {
      dispatch(deleteGeometryRow(row?._id, geometryData?.row?._id));
    }
    setDeleteAction(false);
    setDeleteRow({});
  };

  const showError = (message, rowId) => {
    dispatch(enqueueSnackbar(message, 'error'));
    setInvalidRows(prev => ({ ...prev, [rowId]: true }));
    return false;
  };

  const checkGeometryType = (row) => {
    if (!row.type) {
      return showError('Type is required', row._id);
    }
    return true;
  };

  const checkTopMD = (row, index) => {
    if (row.top_md === undefined || row.top_md === null) {
      return showError('Top MD is required', row._id);
    }
    if (row?.isNewRow) {
      if (index === 0 && row?.top_md !== 0 && properties.length === 1) {
        return showError('Start MD of the first item should be 0', row._id);
      }
    } else {
      if (index === 0 && row?.top_md !== 0) {
        return showError('Start MD of the first item should be 0', row._id);
      }
    }
    return true;
  };

  const checkShoeMD = (row) => {
    if (row.shoe_md === undefined || row.shoe_md === null) {
      return showError('Shoe MD is required', row._id);
    }
    if (row.shoe_md <= row.top_md) {
      return showError('End MD should be greater than Start MD', row._id);
    }
    return true;
  };

  const checkID = (row) => {
    const minId = geometry?.units?.id?.min;
    const maxId = geometry?.units?.id?.max;
    if (row.id < minId) {
      return showError(`ID should be greater than ${minId} ${geometry?.units?.id?.label}`, row._id);
    }
    if (row.id > maxId) {
      return showError(`ID should be smaller than ${maxId} ${geometry?.units?.id?.label}`, row._id);
    }
    return true;
  };

  const checkGeometryConstraints = (row, properties, currentIndex) => {
    if (properties.length) {
      if (row.isNewRow) {
        if (row.id > properties.at(-1).id) {
          if (row?.type === 'Open Hole') {
            if (row.id <= properties.at(-1)?.id * 2) {
              setSaveOpenHoleID({
                open: true,
                data: {
                  current: row,
                  previous: properties.at(-1)
                }
              });
              return false;
            } else {
              return showError(`Open Hole ID cannot be more than twice of its previous row's ID ${properties.at(currentIndex - 1).id} ${geometry?.units?.id?.label}`, row._id);
            }
          }
          return showError(`ID ${row.id} ${geometry?.units?.id?.label} should be less than next row ID ${properties.at(-1).id} ${geometry?.units?.id?.label}`, row._id);
        }
        if (row.top_md > properties.at(-1).shoe_md) {
          return showError(`Next row Start MD should not be larger than previous row End MD ${properties.at(-1).shoe_md} ${geometry?.units?.shoe_md?.label}`, row._id);
        }
      } else {
        if (row.id < properties.at(currentIndex + 1)?.id) {
          return showError(`ID should be greater than next row ID ${properties.at(currentIndex + 1).id} ${geometry?.units?.id?.label}`, row._id);
        }
        if (currentIndex > 0 && row.id > properties.at(currentIndex - 1)?.id) {
          if (row?.type === 'Open Hole') {
            if (row.id <= properties.at(currentIndex - 1)?.id * 2) {
              setSaveOpenHoleID({
                open: true,
                data: {
                  current: row,
                  previous: properties.at(currentIndex - 1)
                }
              });
              return false;
            } else {
              return showError(`Open Hole ID cannot be more than twice of its previous row's ID ${properties.at(currentIndex - 1).id} ${geometry?.units?.id?.label}`, row._id);
            }
          }
          return showError(`ID should be less than previous row ID ${properties.at(currentIndex - 1).id} ${geometry?.units?.id?.label}`, row._id);
        }
        if (row.top_md > properties.at(currentIndex - 1).shoe_md) {
          return showError(`Next row Start MD should not be larger than previous row End MD ${properties.at(currentIndex - 1).shoe_md} ${geometry?.units?.shoe_md?.label}`, row._id);
        }
      }
    }
    return true;
  };

  const validateGeometryData = (row, properties) => {
    const currentIndex = properties.findIndex(item => item._id === row._id);

    return (
      checkGeometryType(row) &&
      checkTopMD(row, currentIndex) &&
      checkShoeMD(row) &&
      checkID(row) &&
      checkGeometryConstraints(row, properties, currentIndex)
    );
  };

  const checkGeometryDataValues = (row) => {
    const invalidFields = [];
    if (!row?.type) invalidFields.push('Type');
    if (row?.top_md === undefined || row?.top_md === null || row?.top_md === '') invalidFields.push('Start MD');
    if (row?.shoe_md === undefined || row?.shoe_md === null || row?.shoe_md === '') invalidFields.push('End MD');
    if (row?.id === undefined || row?.id === null || row?.id === '') invalidFields.push('ID');

    if (invalidFields.length === 0) {
      for (const key in row) {
        if (typeof row[key] === 'string' && !isNaN(row[key])) {
          row[key] = row[key] === '' ? '' : Number(row[key]);
        }
      }
      return validateGeometryData(row, properties);
    } else {
      setInvalidRows(prev => ({ ...prev, [row._id]: true }));
      dispatch(
        enqueueSnackbar(
          `Please enter the following details: ${invalidFields.join(', ')}`,
          'error',
          new Date().getTime() + Math.random()
        )
      );
      return false;
    }
  };

  const saveGeometry = (row) => {
    if (checkGeometryDataValues(row) || saveOpenHoleID?.open) {
      // eslint-disable-next-line multiline-ternary
      const converted = {
        ...row,
        top_md: convertUnitSystem(row.top_md, geometry?.units?.top_md.conversionFactor),
        shoe_md: convertUnitSystem(row.shoe_md, geometry?.units?.shoe_md.conversionFactor),
        id: convertUnitSystem(row.id, geometry?.units?.id.conversionFactor),
      };

      const payload = unit !== UNIT_SYSTEM.US ? converted : row;
      if (payload?.isNewRow) {
        delete payload?.isNewRow;
        dispatch(createGeometry(payload, geometryData?.row?._id));
        setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      } else {
        delete payload?.isDirty;
        dispatch(updateGeometry(payload, geometryData?.row?._id));
        setInvalidRows(prev => ({ ...prev, [row._id]: false }));
      }
      if (saveOpenHoleID?.open) {
        setSaveOpenHoleID({
          open: false,
          data: {}
        });
      }
    }
  };

  const closeHandler = () => {
    if (unSavedExists) {
      handleClose(false);
      return;
    }
    onCloseClick();
  };

  const getModalTitle = () => {
    return (
      <Grid container alignItems="center" justifyContent="space-between">
          <Grid item>
            {geometryData?.row?.name || ''} | Geometry <CreatedByInfoDialog data={geometry?.createdDetails} loading={geometry?.loading || disableAction} />
          </Grid>
          <Grid item>
          <Tooltip title={TOOLTIPS.wellsObservationPoints}>
            <span>
              <IconButton
                disabled={disableAction}
                onClick={() => {
                  setWellsObservationPointsModal(true);
                }}>
                <ScatterPlot fontSize='large' />
              </IconButton>
            </span>
          </Tooltip>
          {isHydraulicsAdmin
            ? (
              <Tooltip title={TOOLTIPS.viewPermissions}>
                <span>
                  <IconButton
                    variant="solid"
                    key="permissions"
                    disabled={disableAction || properties?.length === 0}
                    sx={{ color: geometry?.permissionId && 'green' }}
                    onClick={() => {
                      setPermissions({
                        open: true,
                        data: {
                          ...geometryData?.row,
                          _id: geometry?._id,
                          permissionId: geometry?.permissionId
                        }
                      });
                    }}
                  >
                    <PersonAddAlt fontSize='large' />
                  </IconButton>
                </span>
              </Tooltip>
              )
            : null}
          <IconButton
              onClick={closeHandler}
            >
              <CancelPresentationRounded fontSize='large'/>
            </IconButton>
          </Grid>
        </Grid>
    );
  };
  const renderActions = ({
    row
  }) => {
    return (
      <Grid container justifyContent="center">
        <IconButton
          variant="solid"
          disabled={!row.isDirty || geometry.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.UPDATE, geometry?.permissions))}
          onClick={() => {
            saveGeometry(row);
          }}
        >
          <SaveRounded />
        </IconButton>
        <IconButton
        variant="solid"
        disabled={geometry.loading || (!row.isNewRow && !checkPermissions(APP_ACTION_TYPES.DELETE, geometry?.permissions))}
        onClick={() => {
          setDeleteAction(true);
          setDeleteRow(row);
        }}
      >
        <DeleteRounded/>
      </IconButton>
      </Grid>
    );
  };
  const uploadCSVFile = (e) => {
    const file = e.target.files[0];
    if (file) {
      e.target.value = null;
      dispatch(importWellsGeometryCSVFIle(file, geometryData?.row?._id));
    }
  };

  const updateData = (id, field, value) => {
    setProperties(prevProperties => {
      const updatedProperties = prevProperties.map(item => {
        if (item._id === id) {
          const isDirty = value !== item[field];
          return {
            ...item,
            [field]: value,
            isDirty
          };
        }
        return item;
      });
      return updatedProperties;
    });
  };

  const renderTypeCell = (params) => {
    return (
      <SelectEditValueTypes
        {...params}
        closeCellAfterSelect={true}
        updateData={updateData}
        data={WELLS_GEOMETRY_TYPE} // limit to 50 chars when adding
      />
    );
  };

  const renderValueCell = (params) => {
    return <SelectEditInputTextField numberFormatOnly {...params} updateData={updateData} />;
  };

  if (apiRef.current === null) apiRef.current = {};

  return (
    <ModalDialog
      title={getModalTitle()}
      isOpen={isOpen}
      onClose={closeHandler}
      dialogClass={classes.dialogModal}
    >
      <Grid container>
      <Grid item className={classes.customTool} xs={1} justifyContent="space-between" sx={{ display: !checkPermissions(APP_ACTION_TYPES.CREATE, geometry?.permissions) && 'none !important' }}>
            <Grid item>
              <Typography variant='button' fontWeight='700' className={geometry.loading || properties.some(item => item?.isDirty === true || item?.isNewRow) ? classes.disableIcon : ''} onClick={handleAddSurvey}>+ Add</Typography>
            </Grid>
            <Grid item>
              <label className={geometry.loading ? classes.disableImportLabel : classes.importLabel} htmlFor='getCSVFile'>Import</label>
              <input type='file' id='getCSVFile' accept={'.csv'} style={{ display: 'none' }} onChange={uploadCSVFile} />
            </Grid>
          </Grid>
        <Grid item xs={12}>
        {
          deleteAction && <DeleteConfirmationModal
            open={deleteAction}
            handleDelete={removeSurvey}
            handleClose={setDeleteAction}
            deleteRow={deleteRow}
          />
        }
        {
            saveOpenHoleID?.open && <ConfirmationModal
              open={saveOpenHoleID.open}
              handleAction={() => {
                saveGeometry(saveOpenHoleID?.data?.current);
              }}
              handleClose={() => {
                setSaveOpenHoleID({
                  open: false,
                  data: null
                });
              }}
              modalLabel={`Open Hole ID is set larger than its previous section ID ${saveOpenHoleID?.data?.previous?.id} ${geometry?.units?.id?.label}. Proceed to save?`}
            />
          }
        <UnsavedChangesModal />
          <Datagrid
            apiRef={apiRef}
            data={properties}
            sx={{
              marginLeft: '-6px',
              '.MuiDataGrid-cell': {
                fontSize: '1rem',
                fontWeight: 900
              },
              '.MuiDataGrid-columnHeaderTitle': {
                fontSize: '1.2rem',
                fontWeight: '900 !important',
              },
              [`& .${gridClasses.cell}`]: {
                py: 1,
              },
            }}
            loading={geometry.loading}
            columns={columns(renderCell, renderActions, renderTypeCell, renderValueCell, geometry.units, unit)}
            onRowClick={(params, evt) => {
              evt.preventDefault();
              evt.stopPropagation();
            }}
            disableStickyHeader={false}
            autoHeight={false}
            getRowClassName={(params) => {
              return invalidRows[params.id] ? classes.invalidRow : '';
            }}
            autoheight
            height={'50vh'}
            editMode="cell"
            pageSize={100}
            toolbar={{
              options: {
                columns: false,
                filters: false,
                density: false,
                export: false
              },
            }}
            enableDirtyLock
            permissions={geometry?.permissions}
          />
        </Grid>
      </Grid>
      {wellsObservationPointsModal
        ? (
          <WellsObservationPointsModal
            data={geometryData?.row}
            isOpen={wellsObservationPointsModal}
            onCloseClick={() => {
              setWellsObservationPointsModal(false);
            }}
            runDetails={{
              hole_depth: holeDepth
            }}
            showWellName
          />
          )
        : null}
      {permissions?.open
        ? (
          <PermissionsModal
            isOpen={permissions?.open}
            data={
              {
                ...permissions?.data,
                collection: PERMISSION_DIALOG_SOURCES?.wellsGeometries,
              }
            }
            onCloseClick={() => {
              setPermissions({
                open: false,
                data: {}
              });
            }}
            refreshParentDialog={() => {
              dispatch(listWellsGeometry(geometryData?.row?._id));
            }}
          />
          )
        : null}
    </ModalDialog>
  );
};
GeometryModal.propTypes = {
  onCloseClick: PropTypes.func.isRequired,
  surveyData: PropTypes.object,
  isOpen: PropTypes.bool.isRequired
};
GeometryModal.defaultProps = {
  surveyData: {}
};
export default GeometryModal;
